import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { withCookies } from 'react-cookie';
import LazyLoad from 'react-lazyload';

import {
    Button,
    Lightbox,
    Modal,
} from 'wanderu-components';
import FlagIcon from 'wanderu-flag-icons';
import { localize } from 'wanderu-i18n';

import Events from './events/LocaleSwitchPromptEvents';
import countries from '../LocaleMenu/config/countries.json';

import styles from './LocaleSwitchPrompt.scss';

const COOKIE_MAX_AGE = 86400 * 365 * 10;

function getLocaleFromLocation(ipLocation = {}) {
    const {
        country = '',
        state = '',
    } = ipLocation;

    if (!country || typeof country !== 'string') {
        return null;
    }

    if (country === 'CA' && state === 'QC') {
        return 'fr-ca';
    }

    const countryData = countries[country.toLowerCase()];

    if (countryData && countryData.defaultLocale) {
        return countryData.defaultLocale;
    }

    return null;
}

class LocaleSwitchPrompt extends Component {
    static propTypes = {
        cookies: PropTypes.shape({
            get: PropTypes.func.isRequired,
            remove: PropTypes.func.isRequired,
            set: PropTypes.func.isRequired,
        }).isRequired,
        currentLocale: PropTypes.string,
        ipLocation: PropTypes.object,
        onLocaleChange: PropTypes.func.isRequired,
        t: PropTypes.func.isRequired,
    };

    static defaultProps = {
        currentLocale: null,
        ipLocation: {},
    };

    constructor(props) {
        super(props);

        this.state = {
            detectedLocale: null,
            isOpen: false,
        };
    }

    componentDidUpdate(prevProps) {
        const {
            cookies,
            currentLocale,
            ipLocation,
        } = this.props;
        const {
            ipLocation: prevIpLocation,
        } = prevProps;

        let detectedLocale = null;

        if (Object.keys(prevIpLocation).length) {
            return;
        }

        detectedLocale = getLocaleFromLocation(ipLocation);
        const { ignoredLocales = [] } = cookies.get('user-i18n-pref') || {};

        if (!!detectedLocale
            && detectedLocale !== currentLocale
            && !ignoredLocales.includes(currentLocale)
        ) {
            this.setState(() => ({
                detectedLocale,
                isOpen: true,
            }));
        }
    }

    _onClose = (e, localeChanged = false) => {
        const {
            cookies,
            currentLocale,
        } = this.props;
        const {
            detectedLocale,
        } = this.state;

        const { ignoredLocales: i18nIgnoredLocales = [] } = cookies.get('user-i18n-pref') || {};
        const ignoredLocales = localeChanged
            ? i18nIgnoredLocales
            : [...i18nIgnoredLocales, currentLocale];

        cookies.set('user-i18n-pref', {
            ignoredLocales,
        }, {
            maxAge: COOKIE_MAX_AGE,
            path: '/',
        });

        this.setState(() => ({
            isOpen: false,
        }));
        Events.localePromptClicked({ currentLocale, detectedLocale });
    };

    _onSwitchLocale = (e) => {
        this.props.onLocaleChange(this.state.detectedLocale);
        this._onClose(e, true);
    };

    render() {
        const {
            t,
        } = this.props;
        const {
            detectedLocale,
            isOpen,
        } = this.state;

        if (!isOpen) {
            return null;
        }

        const countryCode = detectedLocale
            ? detectedLocale.split('-')[1]
            : '';

        return (
            <div
                aria-describedby="localePrompt"
                role="alertdialog"
            >
                <Modal>
                    <Lightbox onHide={this._onClose}>
                        <LazyLoad height={14} once>
                            <FlagIcon
                                className={styles.flag}
                                code={countryCode}
                            />
                        </LazyLoad>

                        <p
                            className={styles.suggestionText}
                            id="localePrompt"
                        >
                            {t('locale-prompt-content', {
                                countryName: t(`${detectedLocale}_locale-prompt`),
                            })}
                        </p>

                        <div className={styles.userOptions}>
                            <Button
                                id="rejectSuggestion"
                                onClick={this._onClose}
                                outline
                            >
                                {t('no_button')}
                            </Button>

                            <Button
                                id="acceptSuggestion"
                                onClick={this._onSwitchLocale}
                            >
                                {t('yes_button')}
                            </Button>
                        </div>
                    </Lightbox>
                </Modal>
            </div>
        );
    }
}

export {
    LocaleSwitchPrompt,
    getLocaleFromLocation,
};
// NOTE: we need withCookies HOC to get the cookies class object. useCookies hook provides a different API
export default localize(withCookies(LocaleSwitchPrompt), ['t'], 'baseApp');
